import { useEffect, useMemo, useState } from "react";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import BusinessIcon from "@mui/icons-material/Business";

import { CancelButton, SaveButton } from "../../../../global/Button";
import ActionBar from "../../../../global/Widget/ActionBar";
import {
  useAdminGetUserCustomerCompanies,
  useAdminSetUserCustomerCompanies,
} from "../../../../../hook/query/userBranch";
import { useGetAllCustomers } from "../../../../../hook/query/useCustomer";
import { useSettingAccessUserPageContextContext } from "../../../../../pages/Setting/Access/User";
import {
  canSaveCustomerCompanyScope,
  hasCustomerCompanyScopeChanges,
  normalizeCustomerUuids,
} from "./customerCompanyScopeRules";

type CustomerCompanyOption = {
  uuid: string;
  customer_code: string;
  company_name: string;
  tax_id?: string | null;
  company_branch_no?: string | null;
  isFallback?: boolean;
};

export default function CustomerCompanyScopeManage() {
  const { uuid } = useSettingAccessUserPageContextContext();
  const [customerUuids, setCustomerUuids] = useState<string[]>([]);

  const customerScopeQuery = useAdminGetUserCustomerCompanies(uuid || undefined, !!uuid);
  const customersQuery = useGetAllCustomers({
    enabled: !!uuid,
    baseParams: {
      status: "active",
      sortBy: "customer_code",
      sortOrder: "ASC",
    },
  });
  const updateCustomerScope = useAdminSetUserCustomerCompanies();

  const assignedCompanies = customerScopeQuery.data?.customer_companies || [];
  const initialCustomerUuids = useMemo(
    () => normalizeCustomerUuids(assignedCompanies.map((company) => company.customer_uuid)),
    [assignedCompanies]
  );
  const customerOptions = useMemo<CustomerCompanyOption[]>(() => {
    const optionMap = new Map<string, CustomerCompanyOption>();

    (customersQuery.data?.data || []).forEach((customer) => {
      if (!customer.uuid) {
        return;
      }

      optionMap.set(customer.uuid, {
        uuid: customer.uuid,
        customer_code: customer.customer_code,
        company_name: customer.company_name,
        tax_id: customer.tax_id,
        company_branch_no: customer.company_branch_no,
      });
    });

    assignedCompanies.forEach((company) => {
      if (optionMap.has(company.customer_uuid)) {
        return;
      }

      optionMap.set(company.customer_uuid, {
        uuid: company.customer_uuid,
        customer_code: company.customer_code,
        company_name: company.company_name,
        tax_id: company.tax_id,
        company_branch_no: company.company_branch_no,
        isFallback: true,
      });
    });

    return Array.from(optionMap.values()).sort((a, b) =>
      a.customer_code.localeCompare(b.customer_code)
    );
  }, [assignedCompanies, customersQuery.data?.data]);
  const selectedCustomerOptions = useMemo<CustomerCompanyOption[]>(() => {
    const optionMap = new Map(customerOptions.map((option) => [option.uuid, option]));

    return customerUuids.map(
      (customerUuid) =>
        optionMap.get(customerUuid) || {
          uuid: customerUuid,
          customer_code: customerUuid,
          company_name: "ไม่พบในข้อมูลลูกค้า",
          isFallback: true,
        }
    );
  }, [customerOptions, customerUuids]);
  const hasChanges = hasCustomerCompanyScopeChanges(customerUuids, initialCustomerUuids);
  const canSave = canSaveCustomerCompanyScope({
    hasSavedUser: !!uuid,
    hasChanges,
    isScopeLoading: customerScopeQuery.isLoading,
    isScopeFetching: customerScopeQuery.isFetching,
    isUpdating: updateCustomerScope.isLoading,
  });

  useEffect(() => {
    if (!uuid) {
      setCustomerUuids([]);
      return;
    }

    if (customerScopeQuery.isSuccess) {
      setCustomerUuids(initialCustomerUuids);
    }
  }, [uuid, customerScopeQuery.isSuccess, customerScopeQuery.dataUpdatedAt, initialCustomerUuids]);

  const handleDeleteCustomer = (customerUuid: string) => {
    setCustomerUuids((prev) => prev.filter((item) => item !== customerUuid));
  };

  const handleCancel = () => {
    setCustomerUuids(initialCustomerUuids);
  };

  const handleSave = async () => {
    if (!canSave || !uuid) {
      return;
    }

    try {
      await updateCustomerScope.mutateAsync({
        user_uuid: uuid,
        customer_uuids: normalizeCustomerUuids(customerUuids),
        access_level: "viewer",
      });
    } catch {
      // Snackbar is handled by the mutation hook.
    }
  };

  if (!uuid) {
    return (
      <Stack height="100%" justifyContent="center" alignItems="center" p={3}>
        <Alert severity="info">บันทึกข้อมูลผู้ใช้งานก่อน จึงจะกำหนดขอบเขตลูกค้าได้</Alert>
      </Stack>
    );
  }

  return (
    <Stack height="100%">
      {(customerScopeQuery.isLoading || customerScopeQuery.isFetching) && <LinearProgress />}
      <Stack flex={1} overflow="auto" spacing={3} p={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: "primary.50",
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon />
          </Box>
          <Box>
            <Typography fontWeight={700}>ขอบเขตข้อมูลลูกค้า</Typography>
            <Typography variant="caption" color="text.secondary">
              กำหนดบริษัทลูกค้าที่ผู้ใช้งานคนนี้มีสิทธิ์เห็นในเมนูวางบิล
            </Typography>
          </Box>
        </Stack>

        {customerScopeQuery.isError && (
          <Alert severity="error">โหลดขอบเขตลูกค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง</Alert>
        )}

        {customersQuery.isError && (
          <Alert severity="warning">โหลดรายการบริษัทลูกค้าไม่สำเร็จ กรุณา refresh อีกครั้ง</Alert>
        )}

        <Alert severity="info">
          การกำหนด Customer Scope จะทำให้ผู้ใช้งานคนนี้เป็นมุมมองลูกค้า เห็นเฉพาะบริษัทที่กำหนด
          และระบบจะไม่อนุญาตให้ตรวจสอบ อนุมัติ แก้ไข หรือยกเลิกรายการชำระเงิน
          หากต้องการให้เป็นผู้ดูแลระบบที่อนุมัติรับชำระได้
          ให้กำหนดสิทธิ์เมนูผ่านแท็บสิทธิ์การใช้งานหรือกลุ่มผู้ใช้งาน โดยไม่ต้องกำหนด Customer Scope
        </Alert>

        {selectedCustomerOptions.length === 0 && (
          <Alert severity="warning">
            ยังไม่ได้กำหนดบริษัทลูกค้า ผู้ใช้ที่เข้าเมนูลูกค้าจะไม่เห็นข้อมูลบิลของบริษัทใด
            ส่วนผู้ใช้งานภายในที่ไม่ได้ใช้มุมมองลูกค้าจะยังใช้สิทธิ์เมนูเดิม
          </Alert>
        )}

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={customerOptions}
          value={selectedCustomerOptions}
          loading={customersQuery.isLoading || customersQuery.isFetching}
          getOptionLabel={(option) => `${option.customer_code} - ${option.company_name}`}
          isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
          onChange={(_, value) => {
            setCustomerUuids(normalizeCustomerUuids(value.map((option) => option.uuid)));
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={`${option.customer_code} - ${option.company_name}`}
                color={option.isFallback ? "warning" : "primary"}
                variant="outlined"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="บริษัทลูกค้า"
              placeholder="ค้นหารหัสหรือชื่อบริษัท"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {(customersQuery.isLoading || customersQuery.isFetching) && (
                      <CircularProgress color="inherit" size={18} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText="ไม่พบบริษัทลูกค้า"
          fullWidth
        />

        <Box>
          <Typography variant="body2" fontWeight={600} mb={1}>
            บริษัทลูกค้าใน scope
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {selectedCustomerOptions.length > 0 ? (
              selectedCustomerOptions.map((customer) => (
                <Chip
                  key={customer.uuid}
                  label={`${customer.customer_code} - ${customer.company_name}`}
                  color={customer.isFallback ? "warning" : "primary"}
                  variant="outlined"
                  onDelete={() => handleDeleteCustomer(customer.uuid)}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                ยังไม่ได้กำหนดบริษัทลูกค้า
              </Typography>
            )}
          </Box>
        </Box>
      </Stack>
      <Divider />
      <ActionBar>
        <CancelButton
          disabled={!hasChanges || updateCustomerScope.isLoading}
          onClick={handleCancel}
        >
          ยกเลิก
        </CancelButton>
        <SaveButton
          loading={updateCustomerScope.isLoading}
          disabled={!canSave}
          onClick={handleSave}
        >
          บันทึก
        </SaveButton>
      </ActionBar>
    </Stack>
  );
}
